import React, { useState, useEffect, useRef, use } from 'react';
import { useForm, useStore, revalidateLogic } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { subDays } from 'date-fns';
import type { NarrativeFormData, NarrativeFormProps, NarrativeType, FormErrors, SelectOption } from './types';
import { projectsApi } from '../../utils/projectsApi';
import { showRequestError } from '../../utils/requestError';
import { getDefaultStoriesTranslationLanguage } from '../../utils/languages';
import { canCollectComments } from '../../utils/contentTypes';
import { 
  BasicInfoGroup, 
  SearchGroup, 
  PlatformsContentGroup, 
  ActorsBehaviorGroup,
  AIAnalysisGroup,
  AdditionalSettingsGroup
} from '../FormGroups';
import { ReactComponent as NarrativeIcon } from '../../pages/NarrativeAddPage/assets/narrative.svg';
import { ReactComponent as ManualIcon } from '../../pages/NarrativeAddPage/assets/manual.svg';
import { ReactComponent as ImpactIcon } from '../../pages/NarrativeAddPage/assets/impact.svg';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useCurrentQuota } from '../../contexts/CurrentQuota';
import { QuotaCheckModal } from '../QuotaCheckModal/QuotaCheckModal';
import { QuotaCheckModalOld } from '../QuotaCheckModalOld/QuotaCheckModalOld';
import { Tooltip as ReactTooltip } from 'react-tooltip';

export const NarrativeForm: React.FC<NarrativeFormProps> = ({
  mode,
  initialData,
  narrativeId,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [currentQuota] = useCurrentQuota();
  const actorGroupRef = useRef<any>(null);
  const actorRef = useRef<any>(null);
  const quillRef = useRef<any>(null);
  const quotaConfirmedRef = useRef<boolean>(false);
  
  const [caseCreation, setCaseCreation] = useState(false);
  const [quotaModal, setQuotaModal] = useState({ isOpen: false, data: null });
  const [isLoading, setIsLoading] = useState(false);
  const [, forceUpdate] = useState({});
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    projectsApi.list().then((data: any[]) => {
      if (!cancelled) setProjectOptions(data.map((p: any) => ({ value: p.id, label: p.name })));
    }).catch((err) => {
      if (!cancelled) {
        showRequestError(err, t('Projects'));
        setProjectOptions([]);
      }
    });
    return () => { cancelled = true; };
  }, []);
  
  
  const notEnoughQuota = currentQuota?.billing_mode && currentQuota?.quota?.ANALYTICAL?.balance <= 0;
  const workspaceConfig = currentUser?.workspace?.config;
  const availableCaseTypes = workspaceConfig?.available_case_types || [];
  const featureFlags = workspaceConfig?.featureflags;
  const limitTMUsage = featureFlags?.includes('limit_tm_usage');

  const narrativeTypes: NarrativeType[] = [
    {
      type: 'NARRATIVE',
      title: t('Narrative'),
      icon: <NarrativeIcon />,
      description: t('A case that automatically collects all data for a certain topic based on your search query. Can contain both boolean and AI search. Is updated every 30 minutes. Supports Stories creation.')
    },
    {
      type: 'MANUAL',
      title: t('Manual'),
      icon: <ManualIcon />,
      description: t('An empty case to which you can manually add publications from search and other cases.')
    },
    {
      type: 'TOPIC_MODELING',
      title: t('Topic modeling'),
      icon: <ImpactIcon />,
      description: t('A case to analyze public reaction to a particular media product.')
    },
  ];

  const defaultNarrativeType = Array.isArray(availableCaseTypes) && availableCaseTypes.length > 0 
    ? narrativeTypes.findIndex(type => type.type === availableCaseTypes[0]) 
    : 0;

  const getDefaultValues = (): NarrativeFormData => ({
    // Basic Info
    name: '',
    description: '',
    isActive: true,
    isManuallyManaged: false,
    currentNarrativeType: defaultNarrativeType,
    reviewStatus: null,

    // Search
    keywords: '',
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
    isCaseOpen: false,
    isMultilang: false,
    nativeLang: { value: '', label: t('Detect Automatically') },

    // Platforms and Content
    sourceTypes: [],
    contentTypes: [],
    languages: [],
    languagesExclude: false,
    collectCommentsToPosts: false,
    collectCommentsMode: 'ALL',
    collectCommentsPerPostLimit: null,
    collectCommentsSamplePercent: null,

    // Actors and Behavior
    countries: [],
    countriesExclude: false,
    sourceGroups: [],
    sourceGroupsExclude: false,
    sources: [],
    sourcesExclude: false,
    geoGroup: [],
    geoGroupExclude: false,
    tags: [],
    tagsExclude: false,

    // Actor Behavior
    feeds: [],
    feedsExclude: false,
    affiliationCountries: [],
    affiliationCountriesExclude: false,
    affiliationAll: false,
    affiliationTypes: [],
    affiliationTypesExclude: false,
    discreditations: [],
    discreditationsExclude: false,
    showBotsOnly: null,

    // AI Analysis - Sentiment
    isAspectBasedSentimentEnabled: false,
    aspectBasedSentimentObject: null,
    aspectBasedSentimentSubject: '',

    // AI Analysis - Stories
    createStories: false,
    storiesCandidatesMinThreshold: 0.8,
    storiesGroupingMaxDistance: 0.64,
    useStoriesGroupingMaxDistance: false,
    storiesVectorsModel: null,
    includeCommentsToStories: false,
    includeChatMessagesToStories: false,
    enableStoriesSummarization: true,
    storiesTranslationLanguage: getDefaultStoriesTranslationLanguage(t, currentUser?.translation_language),

    // AI Analysis - Topic Modeling
    topicModelingEntity: null,
    topicModelingTranslationLanguage: getDefaultStoriesTranslationLanguage(t, currentUser?.translation_language),

    // Additional Settings - Date Range
    addDateRangeSettings: false,
    dateRangePreset: null,
    startDatePreset: null,
    endDatePreset: null,

    // Additional Settings - Sorting
    addDefaultSorting: false,
    defaultSorting: {
      fieldName: 'rating',
      isAscending: false,
    },

    // Additional Settings - Widgets
    widgetsSettings: false,
    summaryPanel: null,
    eventCard: null,
    opinionCard: null,
    sentimentBar: [],
    geoWidget: true,
    topActors: null,
    actorTypes: true,
    evolutionChart: true,
    evolutionTabs: [],
    evolutionBreakdowns: [],

    // Other
    sentiment: [],
    applySupervisedClassifier: false,

    // Project assignment
    project: null,

    // Edit mode only
    refreshWholeCase: false,
    organizations: [],

    // Merge with initial data if provided
    ...initialData,
  });

  const validateForm = (value: NarrativeFormData) => {
    const errors: React.ReactNode[] = [];
    
    const currentType = narrativeTypes[value.currentNarrativeType || 0]?.type;
    if (['NARRATIVE', 'TOPIC_MODELING'].includes(currentType)) {
      const hasKeywords = value.keywords?.trim() && value.keywords !== null;
      const hasSources = value.sources && value.sources.length > 0;
      const hasSourceGroups = value.sourceGroups && value.sourceGroups.length > 0;
      const hasDiscreditations = value.discreditations && value.discreditations.length > 0;
      const hasAffiliationCountries = value.affiliationCountries && value.affiliationCountries.length > 0;
      const hasAffiliationTypes = value.affiliationTypes && value.affiliationTypes.length > 0;

      if (!hasKeywords && !hasSources && !hasSourceGroups && !hasDiscreditations && !hasAffiliationCountries && !hasAffiliationTypes) {
        const scrollToField = (fieldName: string) => {
          let element: Element | null = null;
          
          const selectors = [
            `[data-field="${fieldName}"]`,
            `[name="${fieldName}"]`,
            `#${fieldName}`,
            `.react-select-container:has([name="${fieldName}"])`,
          ];
          
          for (const selector of selectors) {
            try {
              if (selector.includes(':contains')) {

                const labels = document.querySelectorAll('label');
                const searchText = fieldName === 'sources' ? 'Individual actors' : 
                                 fieldName === 'sourceGroups' ? 'Actor groups' : 'Keywords';
                element = Array.from(labels).find(label => 
                  label.textContent?.includes(searchText)
                )?.closest('.form-element') || null;
              } else {
                element = document.querySelector(selector);
              }
              if (element) break;
            } catch (e) {
              console.warn(`Selector failed: ${selector}`, e);
            }
          }
          
          if (element) {
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (fieldName === 'sources' && actorRef.current) {
              setTimeout(() => {
                actorRef.current.focus();
              }, 500);
            } else if (fieldName === 'sourceGroups' && actorGroupRef.current) {
              setTimeout(() => {
                actorGroupRef.current.focus();
              }, 500);
            } else if (fieldName === 'keywords' && quillRef.current) {
              setTimeout(() => {
                quillRef.current.focus();
              }, 500);
            }
          }
        };

        errors.push(
          <span className='validation-error' key="narrative-validation">
            <span className='error-message-title'>{t('You need to fill in at least one of the following fields: ')}</span>
            <div className='error-message-buttons'>
              <button
                type="button"
                className='error-message-button'
                onClick={() => scrollToField('keywords')}
              >
                {t('Add keywords')}
              </button>
              <button
                type="button"
                className='error-message-button'
                onClick={() => scrollToField('sources')}
              >
                {t('Select actors')}
              </button>
              <button
                type="button"
                className='error-message-button'
                onClick={() => scrollToField('sourceGroups')}
              >
                {t('Select actor groups')}
              </button>
              <button
                type="button"
                className='error-message-button'
                onClick={() => scrollToField('discreditations')}
              >
                {t('Select high-risk')}
              </button>
              <button
                type="button"
                className='error-message-button'
                onClick={() => scrollToField('affiliationCountries')}
              >
                {t('Select state-affiliated')}
              </button>
              <button
                type="button"
                className='error-message-button'
                onClick={() => scrollToField('affiliationTypes')}
              >
                {t('Select affiliation category')}
              </button>
            </div>
          </span>
        );
      }
    }

    // Field-level errors reported from the form validator are cleared on the
    // next submit even if the input is hidden (e.g. mode switched to "All").
    const fieldErrors: Record<string, string> = {};
    if (value.collectCommentsToPosts && canCollectComments(value.contentTypes, value.sourceTypes)) {
      if (value.collectCommentsMode === 'NUMBER' && value.collectCommentsPerPostLimit == null) {
        fieldErrors.collectCommentsPerPostLimit = 'Please fill in the field';
      }
      if (value.collectCommentsMode === 'PERCENT' && value.collectCommentsSamplePercent == null) {
        fieldErrors.collectCommentsSamplePercent = 'Please fill in the field';
      }
    }

    if (errors.length === 0 && Object.keys(fieldErrors).length === 0) return undefined;

    return { form: errors.length > 0 ? errors : undefined, fields: fieldErrors };
  };

  const form = useForm({
    defaultValues: getDefaultValues(),
    onSubmit: async ({ value }) => {
      const shouldConfirmQuota = quotaConfirmedRef.current;
      
      if (shouldConfirmQuota) {
        setCaseCreation(true);
      }

      setIsLoading(true);

      try {
        const result = await onSubmit({
          ...value,
          confirm_quota_usage: shouldConfirmQuota,
        });

        if (result && !result?.id) {
          setQuotaModal({ isOpen: true, data: result });
        } else {
          setCaseCreation(false);
          quotaConfirmedRef.current = false;
        }
      } catch {
        setCaseCreation(false);
        quotaConfirmedRef.current = false;
      } finally {
        setIsLoading(false);
      }
    },
    onSubmitInvalid: ({ formApi }) => {
      const fieldsMeta = formApi.state.fieldMeta;

      const firstInvalidField = Object.keys(fieldsMeta).find(
        (fieldName) => {
          const meta = fieldsMeta[fieldName as keyof typeof fieldsMeta];
          return meta && Array.isArray(meta.errors) && meta.errors.length > 0;
        }
      )
      
      if (firstInvalidField) {
        const element = document.querySelector(`[id="${firstInvalidField}"]`)
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          })
    
          ;(element as HTMLElement).focus()
        }
      }
    },
    validators: {
      onSubmit: ({ value }: { value: NarrativeFormData }) => validateForm(value)
    },
    validationLogic: revalidateLogic({
    mode: 'submit',
    modeAfterSubmission: 'blur',
  }),
  });

  // The workspace user (and their translation language) can load after the form
  // is mounted, so apply the default here as well. Skipped per field when the
  // case already has a saved language (initialData) or the user has changed it.
  useEffect(() => {
    const defaultLanguage = getDefaultStoriesTranslationLanguage(t, currentUser?.translation_language);
    const languageFields = ['storiesTranslationLanguage', 'topicModelingTranslationLanguage'] as const;

    languageFields.forEach((fieldName) => {
      if (initialData?.[fieldName]) return;
      if (form.getFieldMeta(fieldName)?.isDirty) return;

      const currentValue = form.getFieldValue(fieldName);
      if (currentValue?.value !== defaultLanguage.value) {
        form.setFieldValue(fieldName, defaultLanguage, { dontUpdateMeta: true });
      }
    });
  }, [form, initialData, currentUser?.translation_language, t]);

  const currentNarrativeType = useStore(form.store, (state: any) => state.values.currentNarrativeType);
  const currentNarrativeTypeData = narrativeTypes[currentNarrativeType || 0];

  const showSearchSection = ['NARRATIVE', 'TOPIC_MODELING'].includes(currentNarrativeTypeData?.type);
  const isTopicModelingEditMode = mode === 'edit' && currentNarrativeTypeData?.type === 'TOPIC_MODELING';

  //do not touch - custom error message does not work without this
  const errors = useStore(form.store, (state: any) => state?.errors ? state.errors : null) as FormErrors<NarrativeFormData> | null;

  // Force re-validation when keywords change
  useEffect(() => {
    form.validateAllFields('change');
  }, [form, form.state.values.keywords, form.state.values.sources, form.state.values.sourceGroups, form.state.values.discreditations, form.state.values.affiliationCountries, form.state.values.affiliationTypes]);

  // Subscribe to form state changes to force re-render
  useEffect(() => {
    const unsubscribe = forceUpdate({});
    return unsubscribe;
  }, [form]);

  const getQuotaModalComponent = () => {
    if (!quotaModal?.isOpen || !currentQuota?.billing_mode) return null;

    const commonProps = {
      isOpen: quotaModal.isOpen,
      isTopicModeling:
        narrativeTypes[currentNarrativeType]?.type === 'TOPIC_MODELING' &&
        limitTMUsage,
      onClose: () => {
        setQuotaModal({ isOpen: false, data: null });
      },
      data: quotaModal.data,
      buttonText: mode === 'add' ? t('Create case') : t('Save'),
      onChange: () => {
        setQuotaModal({ isOpen: false, data: null });
        quotaConfirmedRef.current = true;
        form.handleSubmit();
      },
    };

    switch (currentQuota.billing_mode) {
      case 'v0.2':
        return <QuotaCheckModal {...commonProps} />;
      case 'v0.1':
        return <QuotaCheckModalOld {...commonProps} />;
      default:
        return null;
    }
  };
  return (
    <div className="narrative-form-root" translate="no">
      <div className="page-header">
        <div className="breadcrumb">
          <div className="breadcrumb">
            <span>
              {mode === 'add' && initialData ? <span onClick={() => window.history.back()}>{t('Search')}</span> : <a href="/narratives">{t('Cases')}</a> }
            </span>
            <span>{mode === 'add' ? t('New') : t('Edit')}</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
        <form
          id='narrative-form'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <BasicInfoGroup form={form} mode={mode} featureFlags={featureFlags} projects={projectOptions} />

          {showSearchSection && (
            <div className="form-wrapper">
              <h2>{t('Search')}</h2>
              <SearchGroup form={form} isCase={true} quillRef={quillRef} isDisabled={isTopicModelingEditMode}/>
              <PlatformsContentGroup form={form} isDisabled={isTopicModelingEditMode}/>
              <ActorsBehaviorGroup form={form} actorGroupRef={actorGroupRef} actorRef={actorRef} isDisabled={isTopicModelingEditMode}/>
            </div>
          )}

          {showSearchSection && (<AIAnalysisGroup form={form} narrativeTypes={narrativeTypes} isDisabled={isTopicModelingEditMode}/>)}

          {showSearchSection && (<AdditionalSettingsGroup form={form} narrativeTypes={narrativeTypes} mode={mode} />)}

          {!form.state.isValid && (Array.isArray(errors) && errors.length > 0) && (
              <div className="error-message-helper">
                {Array.isArray(errors) && errors.length > 0 ? errors.map((error, index) => (
                  <div key={index}>{error}</div>
                )) : <div>{form.state.errors}</div>}
              </div>
            )}

          <div className="form-element">
            <div className="submit-wrapper">
              <button
                type="submit"
                className="new-button"
                disabled={
                  notEnoughQuota || form.state.isSubmitting || quotaModal.isOpen
                }
                data-tooltip-id="save-case"
                data-tooltip-content={
                  !notEnoughQuota 
                    ? (mode === 'add' ? t('Create case') : t('Save'))
                    : t('No quota left')
                }
              >
                {form.state.isSubmitting && caseCreation
                  ? (mode === 'add' ? t('Creating case...') : t('Saving case...'))
                  : isLoading && !caseCreation 
                  ? t('Checking quota...')
                  : mode === 'add' && !isLoading && !caseCreation
                  ? t('Create case')
                  : t('Save case')}
              </button>
              <ReactTooltip id="save-case" place="bottom" />
            </div>
          </div>
        </form>

        {getQuotaModalComponent()}

        <ReactTooltip />
      </div>
    </div>
  );
};